import JSZip from 'jszip'
import { createElement } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { QRCode } from 'react-qr-code'
import { useQuery } from 'react-query'
import useToastr from '../../../hooks/useToastr'
import CertificateListService from '../../../services/CertificateListService'

const MONTSERRAT_CSS_URL =
  'https://fonts.googleapis.com/css2?family=Montserrat:wght@700&display=swap'

export type DownloadFormat = 'svg' | 'png' | 'zip'

type QrGeneratedAssets = {
  svgBlob: Blob
  pngBlob: Blob
}

type SupportedScheme = {
  certificateScheme: string
  prefix: string
}

export function useCertificateQrCodeGenerator(certificate: {
  certificateType: string
  parties: {
    relationshipType: string
    partySiteNumber: string
  }[]
} | null | undefined) {
  const { certificateType, parties } = certificate ?? {
    certificateType: '',
    parties: [],
  }
  const productIqUrl = process.env.REACT_APP_PRODUCT_IQ_URL
  const { showSuccess, showError } = useToastr()
  const templatePath = '/images/certificate_qr_code_template.svg'
  const labelSelector = '#qr-label-text'
  const qrImageSelector = '#qr-code-image'
  const brandOwnerPsn = parties?.find(p => p.relationshipType === 'Brand Owner')?.partySiteNumber
  const isDisabled = !certificateType || !brandOwnerPsn
  const { data: supportedSchemes, isLoading: isSupportedSchemesLoading } = useQuery(
    ['qr-code-supported-cert-schemes'],
    async () => {
      const response = await CertificateListService.getQRCodeSupportedCertSchemes()
      return response as SupportedScheme[]
    },
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  )

  const getTemplateSvg = async () => {
    const response = await fetch(templatePath)
    if (!response.ok) {
      throw new Error(`Failed to load SVG template: ${templatePath}`)
    }
    return response.text()
  }

  const getPrefix = () => {
    return supportedSchemes?.find(s => s.certificateScheme === certificateType)?.prefix ?? ''
  }

  const isSchemeSupported = supportedSchemes?.some(s => s.certificateScheme === certificateType)
  const qrValue = `${productIqUrl}/qr/${certificateType}/${brandOwnerPsn}`.replace(/\s+/g, '_')
  const baseName = 'QR_Code'

  const getSerializedSvg = async () => {
    const rawTemplateSvg = await getTemplateSvg()
    const parser = new DOMParser()
    const svgDocument = parser.parseFromString(rawTemplateSvg, 'image/svg+xml')
    const svgElement = svgDocument.documentElement as any

    const labelNode = svgElement.querySelector(labelSelector)
    if (labelNode) {
      labelNode.textContent = getPrefix()
    }

    const qrMarkup = renderToStaticMarkup(createElement(QRCode, { value: qrValue, size: 256 }))
    const qrDataUrl = `data:image/svg+xml;base64,${utf8ToBase64(qrMarkup)}`

    const qrNode = svgElement.querySelector(qrImageSelector)
    if (qrNode) {
      qrNode.setAttribute('href', qrDataUrl)
      qrNode.setAttribute('xlink:href', qrDataUrl)
    }

    return {
      serializedSvg: new XMLSerializer().serializeToString(svgElement),
      svgElement,
    }
  }

  const createPngBlob = async (serializedSvg: string, svgElement: SVGSVGElement) => {
    const svgWithEmbeddedFont = await embedMontserratFont(serializedSvg)
    const svgBlob = new Blob([svgWithEmbeddedFont], {
      type: 'image/svg+xml;charset=utf-8',
    })
    const svgUrl = URL.createObjectURL(svgBlob)

    const image = new Image()
    image.decoding = 'async'
    image.src = svgUrl

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('Could not render SVG for PNG export.'))
    })

    const widthFromAttr = Number.parseFloat(svgElement.getAttribute('width') ?? '')
    const heightFromAttr = Number.parseFloat(svgElement.getAttribute('height') ?? '')
    const viewBox = svgElement.viewBox.baseVal

    const width = Number.isFinite(widthFromAttr) ? widthFromAttr : viewBox?.width || 300
    const height = Number.isFinite(heightFromAttr) ? heightFromAttr : viewBox?.height || 300

    const scale = 4
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(width * scale))
    canvas.height = Math.max(1, Math.round(height * scale))

    const context = canvas.getContext('2d')
    if (!context) {
      URL.revokeObjectURL(svgUrl)
      throw new Error('Could not create canvas context.')
    }

    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    URL.revokeObjectURL(svgUrl)

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Could not create PNG blob.'))
          return
        }

        resolve(blob)
      }, 'image/png')
    })
  }

  const createAssets = async (): Promise<QrGeneratedAssets> => {
    const svgInfo = await getSerializedSvg()
    const svgBlob = new Blob([svgInfo.serializedSvg], {
      type: 'image/svg+xml;charset=utf-8',
    })
    const pngBlob = await createPngBlob(svgInfo.serializedSvg, svgInfo.svgElement)

    return { svgBlob, pngBlob }
  }

  const downloadBlob = (blob: Blob, filename: string) => {
    const blobUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = blobUrl
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(blobUrl)
  }

  const createZipBlob = async () => {
    try {
      const { svgBlob, pngBlob } = await createAssets()
      const zip = new JSZip()

      zip.file(`${baseName}.svg`, svgBlob)
      zip.file(`${baseName}.png`, pngBlob)

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 },
      })

      return zipBlob
    } catch (error) {
      showError('Download failed.', 'Download unsuccessful. Please try again.')
      throw error
    }
  }

  const defaultZipFilename = `Cert_Scheme_QR_Code.zip`

  const downloadZip = async () => {
    const zipBlob = await createZipBlob()
    downloadBlob(zipBlob, defaultZipFilename)
    showSuccess('Download complete!', 'Download completed successfully.')
  }

  const createZipFile = async () => {
    const zipBlob = await createZipBlob()
    return new File([zipBlob], defaultZipFilename)
  }

  return {
    isSchemeSupported,
    isDisabled,
    isSupportedSchemesLoading,
    createZipBlob,
    createZipFile,
    downloadZip,
  }
}

async function embedMontserratFont(svgMarkup: string): Promise<string> {
  try {
    const fontBase64 = await getMontserratWoff2Base64()

    if (!fontBase64) {
      return svgMarkup
    }

    const embeddedStyle =
      `<style xmlns="http://www.w3.org/1999/xhtml">` +
      `@font-face{font-family:'Montserrat';font-style:normal;font-weight:700;` +
      `src:url(data:font/woff2;base64,${fontBase64}) format('woff2');}` +
      `</style>`

    if (/<style[^>]*>@import[^<]*<\/style>/i.test(svgMarkup)) {
      return svgMarkup.replace(/<style[^>]*>@import[^<]*<\/style>/i, embeddedStyle)
    }

    return svgMarkup.replace(/<svg([^>]*)>/i, `<svg$1>${embeddedStyle}`)
  } catch {
    return svgMarkup
  }
}

async function getMontserratWoff2Base64(): Promise<string | null> {
  const cssResponse = await fetch(MONTSERRAT_CSS_URL)
  const cssText = await cssResponse.text()
  const latinFaceBlockMatch = cssText.match(
    /@font-face\s*\{[^}]*unicode-range\s*:\s*[^;]*U\+0000-00FF[^;]*;[^}]*\}/i
  )
  const woff2UrlRegex = /url\((https?:\/\/[^)]+\.woff2)\)\s*format\('woff2'\)/i
  const fontUrlMatch = (latinFaceBlockMatch?.[0] ?? cssText).match(woff2UrlRegex)

  if (!fontUrlMatch) {
    return null
  }

  const fontResponse = await fetch(fontUrlMatch[1])
  const fontBuffer = await fontResponse.arrayBuffer()
  return arrayBufferToBase64(fontBuffer)
}

function utf8ToBase64(value: string): string {
  const bytes = new TextEncoder().encode(value)
  let binary = ''
  const chunkSize = 0x8000

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode(...chunk)
  }

  return window.btoa(binary)
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunkSize = 0x8000

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode(...chunk)
  }

  return window.btoa(binary)
}
